import React, { useEffect, useMemo, useState } from "react";
import { Bar, Line } from "react-chartjs-2";
import DonutChart from "./DonutChart";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
} from "chart.js";
import { analyticsAPI } from "../utils/analyticsAPI";
import "./ReportContent.css";

// Safe to register multiple times; Chart.js will ignore duplicates.
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
);

/**
 * Render report body content, including optional structured sections and charts.
 *
 * Backwards-compatible:
 * - If report.content_type !== "json" or JSON parsing fails, falls back to plain text.
 * - Charts are optional; if none are defined, only text is rendered.
 */
function ReportContent({ report, organizationId }) {
  const [dashboardData, setDashboardData] = useState(null);
  const [loadingCharts, setLoadingCharts] = useState(false);
  const [chartError, setChartError] = useState(null);

  const { structured, sections, charts } = useMemo(() => {
    if (!report || report.content_type !== "json" || !report.content) {
      return { structured: null, sections: [], charts: [] };
    }
    try {
      const parsed = JSON.parse(report.content);
      const parsedSections = Array.isArray(parsed.sections)
        ? parsed.sections
        : [];
      const parsedCharts = Array.isArray(parsed.charts) ? parsed.charts : [];
      return {
        structured: parsed,
        sections: parsedSections,
        charts: parsedCharts,
      };
    } catch {
      // Fallback to legacy behaviour
      return { structured: null, sections: [], charts: [] };
    }
  }, [report]);

  // Load organization dashboard data if any chart needs it
  useEffect(() => {
    if (!organizationId || charts.length === 0) return;

    let cancelled = false;
    (async () => {
      try {
        setLoadingCharts(true);
        const resp = await analyticsAPI.getDashboard(organizationId);
        if (cancelled) return;
        setDashboardData(resp?.data || resp || null);
        setChartError(null);
      } catch (err) {
        if (!cancelled) {
          setChartError(err?.message || "Failed to load chart data");
        }
      } finally {
        if (!cancelled) {
          setLoadingCharts(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [organizationId, charts.length]);

  const hasStructured = !!structured;

  const renderRichText = (text) => {
    if (!text) return null;

    const paragraphs = String(text)
      .split(/\n{2,}/)
      .map((block) => block.trim())
      .filter(Boolean);

    return paragraphs.map((block, idx) => {
      const lines = block.split("\n").map((l) => l.trim());
      const isList =
        lines.length > 1 &&
        lines.every(
          (l) => l.startsWith("- ") || l.startsWith("* ") || l.startsWith("• "),
        );

      if (isList) {
        return (
          <ul key={`list-${idx}`}>
            {lines.map((l, i) => (
              <li key={i}>{l.replace(/^[-*•]\s*/, "")}</li>
            ))}
          </ul>
        );
      }

      if (block.startsWith(">")) {
        return (
          <blockquote key={`quote-${idx}`}>
            {block.replace(/^>\s*/, "")}
          </blockquote>
        );
      }

      return (
        <p key={`p-${idx}`}>
          {block.split("\n").map((line, i) => (
            <React.Fragment key={i}>
              {line}
              {i < block.split("\n").length - 1 && <br />}
            </React.Fragment>
          ))}
        </p>
      );
    });
  };

  const renderPlainText = () => {
    if (!report?.content) return null;
    return <div className="report-body">{renderRichText(report.content)}</div>;
  };

  const buildInventoryStatusPie = () => {
    const inv = dashboardData?.inventory;
    if (!inv || !inv.status_breakdown) return null;

    const breakdown = inv.status_breakdown;
    const data = {
      labels: ["Finished", "Urgent", "GIP (Good in Progress)", "Dead"],
      datasets: [
        {
          data: [
            breakdown.finished || 0,
            breakdown.urgent || 0,
            breakdown.gip || 0,
            breakdown.dead || 0,
          ],
          backgroundColor: [
            "rgba(34, 197, 94, 0.85)",
            "rgba(239, 68, 68, 0.85)",
            "rgba(59, 130, 246, 0.85)",
            "rgba(107, 114, 128, 0.85)",
          ],
        },
      ],
    };

    return (
      <div style={{ padding: "12px 4px 4px" }}>
        <DonutChart data={data} height="260px" />
      </div>
    );
  };

  const buildManualChartData = (chart) => {
    const points = Array.isArray(chart.dataPoints) ? chart.dataPoints : [];
    if (points.length === 0) return null;

    const labels = points.map((p) => p.label || "");
    const values = points.map((p) => Number(p.value) || 0);

    const palette = [
      "rgba(59, 130, 246, 0.85)",
      "rgba(34, 197, 94, 0.85)",
      "rgba(239, 68, 68, 0.85)",
      "rgba(245, 158, 11, 0.85)",
      "rgba(139, 92, 246, 0.85)",
      "rgba(16, 185, 129, 0.85)",
    ];

    const backgroundColor =
      chart.type === "line"
        ? "rgba(59, 130, 246, 0.2)"
        : labels.map((_, idx) => palette[idx % palette.length]);

    const borderColor =
      chart.type === "line"
        ? "rgb(59, 130, 246)"
        : labels.map((_, idx) =>
            palette[idx % palette.length].replace("0.85", "1"),
          );

    return {
      labels,
      datasets: [
        {
          label: chart.value_field_name || chart.yLabel || "Value",
          data: values,
          backgroundColor,
          borderColor,
          borderWidth: 2,
          tension: 0.25,
        },
      ],
    };
  };

  const buildChartOptions = (chart) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          padding: 12,
          font: { size: 12 },
          usePointStyle: true,
        },
      },
    },
    scales: {
      x: {
        title: {
          display: !!(chart.label_field_name || chart.label_field),
          text: chart.label_field_name || chart.label_field || "",
          font: { size: 13, weight: "bold" },
          padding: { top: 6 },
        },
        ticks: { font: { size: 11 } },
      },
      y: {
        beginAtZero: true,
        title: {
          display: !!(
            chart.value_field_name ||
            chart.value_field ||
            chart.yLabel
          ),
          text:
            chart.value_field_name || chart.value_field || chart.yLabel || "",
          font: { size: 13, weight: "bold" },
          padding: { bottom: 6 },
        },
        ticks: { font: { size: 11 } },
      },
    },
  });

  const renderChartBlock = (chart) => {
    const title = chart.title || "Chart";

    // If this chart uses org analytics, we may need dashboard data
    const needsDashboardData =
      chart.source === "organization_dashboard" && chart.metricKey;

    if (needsDashboardData && !dashboardData && loadingCharts) {
      return (
        <div className="card" style={{ marginTop: "16px" }}>
          <h4 style={{ marginBottom: "8px" }}>{title}</h4>
          <p style={{ color: "var(--text-grey)" }}>Loading chart data...</p>
        </div>
      );
    }

    if (needsDashboardData && chartError) {
      return (
        <div className="card" style={{ marginTop: "16px" }}>
          <h4 style={{ marginBottom: "8px" }}>{title}</h4>
          <p style={{ color: "var(--danger-red)" }}>
            {chartError || "Unable to load chart data."}
          </p>
        </div>
      );
    }

    let chartContent = null;

    // 1) Charts with dataPoints (manual or dataset-sourced)
    if (
      !chart.source ||
      chart.source === "manual" ||
      chart.source === "dataset"
    ) {
      const data = buildManualChartData(chart);
      if (data) {
        const type = chart.type || "bar";
        const chartOptions = buildChartOptions(chart);
        if (type === "line") {
          chartContent = (
            <div style={{ height: "300px", padding: "12px 4px 4px" }}>
              <Line data={data} options={chartOptions} />
            </div>
          );
        } else if (type === "pie") {
          chartContent = (
            <div style={{ padding: "12px 4px 4px" }}>
              <DonutChart data={data} height="300px" />
            </div>
          );
        } else {
          // default to bar
          chartContent = (
            <div style={{ height: "300px", padding: "12px 4px 4px" }}>
              <Bar data={data} options={chartOptions} />
            </div>
          );
        }
      }
    }

    // 2) Inventory status chart sourced from organization dashboard
    if (
      !chartContent &&
      chart.type === "pie" &&
      chart.source === "organization_dashboard" &&
      chart.metricKey === "inventory_status"
    ) {
      chartContent = buildInventoryStatusPie();
    }

    if (!chartContent) {
      return (
        <div className="card" style={{ marginTop: "16px" }}>
          <h4 style={{ marginBottom: "8px" }}>{title}</h4>
          <p style={{ color: "var(--text-grey)" }}>
            Chart configuration not recognized or data unavailable.
          </p>
        </div>
      );
    }

    return (
      <div className="card" style={{ marginTop: "16px" }}>
        <h4 style={{ marginBottom: "8px" }}>{title}</h4>
        {chart.description && (
          <p
            style={{
              color: "var(--text-grey)",
              marginTop: 0,
              marginBottom: "8px",
            }}
          >
            {chart.description}
          </p>
        )}
        {chartContent}
      </div>
    );
  };

  if (!hasStructured) {
    return renderPlainText();
  }

  return (
    <div className="report-body">
      {/* Structured text sections */}
      {sections.map((section) => (
        <div key={section.id || section.title} className="report-section">
          {section.title && (
            <div className="report-section-title">
              <span>{section.title}</span>
            </div>
          )}
          {section.content && renderRichText(section.content)}
        </div>
      ))}

      {/* Charts */}
      {charts.length > 0 && (
        <div style={{ marginTop: "16px" }}>
          <div className="report-visuals-heading">Visual Insights</div>
          {charts.map((chart) => (
            <React.Fragment key={chart.id || chart.title}>
              {renderChartBlock(chart)}
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Fallback plain text if no sections provided but legacy content exists */}
      {sections.length === 0 && !charts.length && renderPlainText()}
    </div>
  );
}

export default ReportContent;
